import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useTenant } from "@/hooks/useTenant";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { format } from "date-fns";
import { de, enUS } from 'date-fns/locale';
import { Calendar, Clock, User, Edit } from "lucide-react";
import { UpdateVisitModal } from "@/components/calendar/UpdateVisitModal";
import type { CalendarVisit } from "@/hooks/useCalendarData";
import { useCalendarVisits } from "@/hooks/useCalendarData";
import { VisitStatusBadge } from "@/components/ui/VisitStatusBadge";
import {
  getCurrentDateInTimeZone,
  getTenantTimeZone,
  toTimeZoneWallClockDate,
} from "@sylva/shared-types";

export const TodayVisits: React.FC = () => {
  const { currentTenant } = useTenant();
  const { t, i18n } = useTranslation();
  const [editingVisit, setEditingVisit] = useState<CalendarVisit | null>(null);
  const [isEditVisitModalOpen, setIsEditVisitModalOpen] = useState(false);
  const tenantTimeZone = getTenantTimeZone(currentTenant?.timezone);

  // Fetch today's visits using the calendar hook
  const { data: todayVisits = [], isLoading: loading, error: queryError } = useCalendarVisits(
    getCurrentDateInTimeZone(tenantTimeZone),
    { teamMemberIds: [], serviceIds: [], searchQuery: '', viewType: 'day' }
  );

  const error = queryError ? t('failedToLoadAppointments') : null;

  // Filter out cancelled visits
  const activeVisits = todayVisits.filter(visit =>
    visit.status.toLowerCase() !== 'cancelled'
  );

  const formatTime = (dateString: string) => {
    return format(
      toTimeZoneWallClockDate(dateString, tenantTimeZone),
      t('timeFormatHourMinute'),
      { locale: i18n.language === 'de' ? de : enUS },
    );
  };

  const handleEditVisit = (visit: CalendarVisit) => {
    setEditingVisit(visit);
    setIsEditVisitModalOpen(true);
  };

  // Section heading lives on the page itself — strong text + a divider on the
  // content below, no boxed/tinted header band (Alea-style section pattern).
  const sectionHeader = (
    <div className="flex items-center gap-2 px-1">
      <Calendar className="h-5 w-5 text-primary" />
      <h2 className="text-lg font-medium tracking-tight text-foreground-strong">
        {t('todaysAppointments')}
      </h2>
    </div>
  );

  if (loading) {
    return (
      <section className="space-y-3">
        {sectionHeader}
        <Card className="overflow-hidden">
          <CardContent>
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
              <div className="text-muted-foreground">{t('loading')}</div>
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  if (error) {
    return (
      <section className="space-y-3">
        {sectionHeader}
        <Card className="overflow-hidden">
          <CardContent>
            <div className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-12 w-12 text-destructive/50 mb-4" />
              <div className="text-destructive font-medium">{error}</div>
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      {sectionHeader}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {activeVisits.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6">
              <Calendar className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground text-center">{t('noAppointmentsToday')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {activeVisits.map((visit) => (
                <div
                  key={visit.id}
                  className={`p-4 transition-colors ${
                    visit.status === 'no_show'
                      ? 'bg-gray-50 hover:bg-gray-100 opacity-75'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 space-y-2">
                      <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${
                        visit.status === 'no_show'
                          ? 'bg-gray-100'
                          : 'bg-primary/10'
                      }`}>
                        <Clock className={`h-3 w-3 ${
                          visit.status === 'no_show'
                            ? 'text-gray-500'
                            : 'text-primary'
                        }`} />
                        <span className={`text-sm font-medium ${
                          visit.status === 'no_show'
                            ? 'text-gray-500'
                            : 'text-primary'
                        }`}>
                          {formatTime(visit.start_time)}
                        </span>
                      </div>
                      <VisitStatusBadge status={visit.status} />
                    </div>

                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="text-xs">
                            {visit.customer_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className={`font-semibold leading-none ${
                            visit.status === 'no_show'
                              ? 'text-gray-500'
                              : 'text-foreground'
                          }`}>
                            {visit.customer_name}
                          </h4>
                          <p className={`text-sm mt-1 ${
                            visit.status === 'no_show'
                              ? 'text-gray-400'
                              : 'text-muted-foreground'
                          }`}>
                            {formatTime(visit.start_time)} - {formatTime(visit.end_time)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditVisit(visit)}
                        className="flex items-center gap-1"
                      >
                        <Edit className="h-3 w-3" />
                        <span className="hidden md:inline">{t('edit')}</span>
                      </Button>
                    </div>
                  </div>

                  {/* Services and team member badges */}
                  <div className="mt-3 flex flex-wrap gap-2 sm:pl-[88px]">
                    {visit.appointments.map((apt) => (
                      <Badge
                        key={apt.id}
                        variant="secondary"
                        className={`flex items-center gap-2 ${
                          visit.status === 'no_show'
                            ? 'bg-gray-100 text-gray-600 border-gray-200'
                            : ''
                        }`}
                      >
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{
                            backgroundColor: visit.status === 'no_show'
                              ? '#9ca3af'
                              : apt.appointment_type_color
                          }}
                        />
                        {apt.appointment_type_name}
                      </Badge>
                    ))}

                    {visit.team_member_name && (
                      <Badge variant="outline" className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {visit.team_member_name}
                      </Badge>
                    )}

                    {!visit.team_member_name && (
                      <Badge variant="secondary" className="flex items-center gap-1 bg-orange-100 text-orange-800 border-orange-200">
                        <User className="h-3 w-3" />
                        {t('unassigned')}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {editingVisit && (
        <UpdateVisitModal
          open={isEditVisitModalOpen}
          onOpenChange={setIsEditVisitModalOpen}
          editingVisit={editingVisit}
          onVisitSaved={() => {
            setIsEditVisitModalOpen(false);
            setEditingVisit(null);
            // Data auto-refreshes via React Query when visit is saved
          }}
        />
      )}
    </section>
  );
};
